/**
@format
@flow
*/
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Button } from '@ziynx/components/buttons';
import { Heading, Body } from '@ziynx/components/typography';
import { eventNames } from '@ziynx/analytics';
import { Constants } from '@ziynx/components/layout';
import { withAppManager, LoadingSpinner, getMeasures } from '@ziynx/components/utilities';
import ActionTypes from '@ziynx/action-types';
import { useForm, Controller } from 'react-hook-form';
import Colors from '@ziynx/theme/colors';
import Card from '@ziynx/components/cards';
import Icon from '@ziynx/components/icons';
import Input from '@ziynx/components/inputs';
import Modal from '@ziynx/components/modals';
import MediaCropper from '@ziynx/components/croppers';
import type { MediaUploadReference, CroppedObject, CroppedArea } from '@ziynx/flow-types/media';
import type { VideoUploadConfig } from '@ziynx/flow-types/companyProfile';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import ModalConstants from './Constants';

type Props = $ReadOnly<{|
  /**
   * Boolean to open modal
   */
  open?: boolean,
  /**
   * Upload config object
   */
  videoUploadConfig: VideoUploadConfig,
  /**
   * Custom function to close modal
   */
  onClose: () => void,

  getMediaId?: (any) => any,
|}>;

const MODAL_PADDING = {
  mobileSmall: `${ModalConstants?.modalPadding}px 16px`,
  mobile: `${ModalConstants?.modalPadding}px 16px`,
  tablet: `${ModalConstants?.modalPadding}px 16px`,
  desktopSmall: `${ModalConstants?.modalPadding}px 24px`,
  default: `${ModalConstants?.modalPadding}px 48px`,
};

// For fallback, full browser support
const VIDEO_WRAPPER_MEASURES = (viewport) => {
  switch (viewport) {
  case 'mobileSmall':
  case 'mobile':
    return '196px';
  case 'tablet':
    return '197px';
  case 'desktopSmall':
    return '360px';
  default:
    return '508px';
  }
};

const BUTTON_WRAPPER_WIDTH = {
  mobileSmall: '256px',
  mobile: '349px',
  tablet: '350px',
  default: '392px',
};

const BUTTON_CONTROL_WIDTH = {
  mobileSmall: '100%',
  mobile: '100%',
  tablet: '168px',
  default: '188px',
};

const StyledModal = styled(Modal)`
  padding: ${({ viewport }) => getMeasures(MODAL_PADDING, viewport)};
  overflow: hidden;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${({ center }) => (center ? 'center' : 'space-between')};
  align-items: center;
  height: 100%;
  position: relative;
`;

const StyledButton = styled(Button)`
  ${({ isMobile, lastChild }) => isMobile && !lastChild && 'margin-bottom: 16px;'};
`;

const StyledHeadingWrapper = styled.div`
  padding-bottom: ${ModalConstants?.titlePaddingBottom}px;
  text-align: center;
`;

const SmallHeadingWrapper = styled.div`
  text-align: center;
  padding-top: 24px;
  padding-bottom: 12px;
  padding-left: 16px;
  padding-right: 16px;
`;

const VideoSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  padding-bottom: 32px;
  height: 100%;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio || '1 / 1'};
  overflow: hidden;
  max-width: 1000px;
  @supports not (aspect-ratio: 1) {
    width: ${({ width }) => width};
    height: auto;
  };
`;

const VideoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  aspect-ratio: 1 / 1;
  ${({ empty }) => empty
    && `border-radius: ${Constants.borderRadiusLarge}px;
    border: 2px dashed ${Colors.GrayVeryLight};`}
  ${({ border }) => border
    && `border-radius: ${Constants.borderRadiusLarge}px;
    border: 1px solid ${Colors.GrayVeryLight};`}
  box-sizing: border-box;
  margin-bottom: 132px;
  @supports not (aspect-ratio: 1) {
    width: ${({ width }) => width};
    height: auto;
    ${({ empty, width }) => empty && `height: ${width};`}
  };
`;

const UploadButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: ${({ viewport }) => getMeasures(BUTTON_WRAPPER_WIDTH, viewport)};
`;

const UploadButtonsWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  justify-content: space-between;
  width: ${({ viewport }) => getMeasures(BUTTON_WRAPPER_WIDTH, viewport)};
`;

const InputWrapper = styled.div`
  padding-bottom: 38px;
`;

const RetryButtonsWrapper = styled(UploadButtonsWrapper)`
  margin-top: 32px;
  margin-bottom: 0;
`;

const CropperWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  position: relative;
`;

const CropperControlsWrapper = styled.div`
  margin-top: ${ModalConstants?.cropperButtonGroupMargin}px;
`;

const CropperButtonsWrapper = styled(UploadButtonsWrapper)`
  margin-top: 40px;
`;

/**
 * Photo or media uploader screen used to upload/edit profile pictures or videos
 */
const UploadVideoModal = (props: ManagedProps<Props>): React$Node => {
  const {
    open, onClose, videoUploadConfig, viewport, getMediaId,
  } = props;

  const {
    destination, mediaUploadReferenceId = '', videoIndex, existingVideo, isChange, referrer, skipCropStep: skipVideoCropStepProp,
  } = videoUploadConfig || {};

  // To adjust the buttons when choosing to change video
  const [isChanging, setIsChanging] = useState(isChange);
  // Keeps track of image being uploaded
  const [uploadedVideo: File | typeof undefined, setUploadedVideo] = useState(undefined);
  const [skipCropStep, setSkipCropStep] = useState(undefined);
  // Image File from inputFile
  const [rawVideo, setRawVideo] = useState('');
  (rawVideo: string | null | ArrayBuffer);
  // State to render the image/video cropper
  const [shouldRenderCropper, setRenderCropper] = useState(false);
  (shouldRenderCropper: boolean);
  // Keeps track of zoom level
  const [zoom, setZoom] = useState(1);
  (zoom: number);
  // Keeps track of croppedAreaPixels
  const [croppedAreaPixels, setCroppedAreaPixels] = useState({});
  (croppedAreaPixels: CroppedArea);

  const dispatch = useDispatch();

  const { control, handleSubmit, formState } = useForm();

  const { isDirty } = formState;

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  const mediaService = useSelector(({ media }) => {
    const videoPending = media[mediaUploadReferenceId] || {};
    const videoSources = videoPending?.sources || {};
    const thumbnail = videoSources?.thumbnail_optimized?.url || videoSources?.thumbnail?.url;
    const croppedVideo = {
      src: videoSources?.mp4?.url ?? '',
      thumbnail: thumbnail ?? '',
    };

    return {
      ...media,
      videoPending,
      croppedVideo,
    };
  }, shallowEqual);

  useEffect(() => {
    if (skipCropStep === undefined) {
      setSkipCropStep(skipVideoCropStepProp);
    }
  }, [skipVideoCropStepProp]);

  useEffect(() => {
    if (isChange) {
      setIsChanging(true);
    } else {
      setIsChanging(false);
    }
  }, [isChange]);

  const _openCropper = (): void => {
    setRenderCropper(true);
  };

  const _closeCropper = (): void => {
    setRenderCropper(false);
  };

  const _renderVideoSection = (videoFileUrl, videoThumbnail): React$Node => {
    const aspectRatio = croppedAreaPixels?.width
      && croppedAreaPixels?.height
      && croppedAreaPixels?.width !== croppedAreaPixels?.height ? `${croppedAreaPixels?.width} / ${croppedAreaPixels?.height}` : ' 1 / 1';
    if (!videoFileUrl) {
      return (
        <VideoWrapper
          width={VIDEO_WRAPPER_MEASURES(viewport)}
          empty
        >
          <Heading medium color={Colors.Gray}>
            No video
          </Heading>
        </VideoWrapper>
      );
    }
    return (
      <VideoSectionWrapper
        width={VIDEO_WRAPPER_MEASURES(viewport)}
        aspectRatio={aspectRatio}
      >
        <Card
          variant="video"
          src={videoFileUrl}
          imageSource={videoThumbnail}
          aspectRatio={aspectRatio}
        />
      </VideoSectionWrapper>
    );
  };

  useEffect(() => {
    if (videoUploadConfig.referrer) {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_change_video,
        properties: {
          referrer,
        },
      });
    }
  }, [videoUploadConfig.referrer]);

  const _removeVideo = (): void => {
    setRawVideo('');
    setZoom(1);
    setCroppedAreaPixels({});
    dispatch({ type: ActionTypes.media.clearVideo });
  };
  const _uploadVideo = (file?: File): void => {
    const mediaUploadReference: MediaUploadReference = {
      uploadReferenceId: mediaUploadReferenceId,
      mediaType: 'video',
      videoFile: uploadedVideo || file, // Check in case race condition hits on large videos
    };
    const body = skipCropStep ? {
      type: 'video/mp4',
      skipScaling: true,
    } : {
      type: 'video/mp4',
      cropDimensions: {
        top: croppedAreaPixels?.y,
        left: croppedAreaPixels?.x,
        width: croppedAreaPixels?.width,
        height: croppedAreaPixels?.height,
      },
    };
    dispatch({
      type: ActionTypes.media.getPresignedUrl.START,
      body,
      mediaUploadReference,
    });
  };

  const _finishCrop = (file?: File): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.cropped_video,
    });
    _uploadVideo(file);
    if (!skipCropStep) _closeCropper();
  };

  const _patchCompanyProfile = (videoTitle: string): void => {
    dispatch({
      type: ActionTypes.company.patch.START,
      body: [
        {
          op: 'replace',
          path: `videos[${videoIndex ?? 0}]`,
          value: {
            title: videoTitle || 'Video',
            video: mediaService?.videoPending?.assetId,
          },
        },
      ],
    });
  };

  const _patchContentVideo = (): void => {
    if (mediaUploadReferenceId === 'contentPost' && mediaService?.videoPending && getMediaId) {
      getMediaId({
        assetId: mediaService?.videoPending?.assetId,
        sources: mediaService?.videoPending?.sources,
      });
    } else {
      dispatch({
        type: ActionTypes.statementItem.patchQuestion.START,
        body: [{
          op: 'replace',
          path: 'inspirationVideo',
          value: mediaService?.videoPending?.assetId,
        }],
        id: mediaUploadReferenceId,
      });
    }
  };
  const _patchOriginal = (): void => {
    const vidObj = mediaService[mediaUploadReferenceId]
    || mediaService?.originalVideo
    || mediaService?.videoPending;
    if (getMediaId) {
      getMediaId({
        assetId: vidObj?.assetId,
        sources: vidObj?.sources,
      });
    }
    if (!mediaUploadReferenceId || (mediaUploadReferenceId === 'originalVideo' && mediaService?.videoPending)) return;

    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.changed_original_video,
    });
    dispatch({
      type: ActionTypes.originals.patch.START,
      body: [{
        op: 'replace',
        path: 'video',
        value: vidObj?.assetId,
      }],
      id: mediaUploadReferenceId,
    });
  };

  const _patchVideoTitle = (newVideoTitle: string): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.changed_video_title,
    });
    dispatch({
      type: ActionTypes.company.patch.START,
      body: [
        {
          op: 'replace',
          path: `videos[${videoIndex ?? 0}].title`,
          value: newVideoTitle,
        },
      ],
    });
  };

  const _onSave = (data: { videoTitle: string }): void => {
    const { videoTitle } = data || {};
    switch (destination) {
    case 'contentVideo':
      _patchContentVideo();
      _removeVideo();
      break;
    case 'originalVideo':
      _patchOriginal();
      break;
    case 'company':
    default:
      if (existingVideo?.uri && !mediaService?.videoPending?.assetId) {
        _patchVideoTitle(videoTitle);
        break;
      } else if (existingVideo?.uri && !isChanging) {
        _patchCompanyProfile(videoTitle);
        break;
      } else {
        _patchCompanyProfile(videoTitle);
        _removeVideo();
        break;
      }
    }

    if (onClose) onClose();
  };

  const _onCancel = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.edit_video_cancelled,
    });
    if (!existingVideo && rawVideo) _removeVideo();
    if (onClose) onClose();
  };

  const _handleCropComplete = (result: CroppedObject): void => {
    setCroppedAreaPixels(result?.croppedAreaPixels);
  };

  const _handleOnZoomChange = (
    event: SyntheticInputEvent<HTMLInputElement>,
  ): void => {
    setZoom(event?.target?.valueAsNumber);
  };

  const BUTTON_WIDTH = getMeasures(BUTTON_CONTROL_WIDTH, viewport);

  /** Used for InputFile. onClick already handled in Krispy */
  const _renderInputFileButton = (): React$Node => (
    <StyledButton
      isMobile={isMobile}
      variant="primary"
      width={BUTTON_WIDTH}
    />
  );

  const _retryUpload = (): void => {
    dispatch({
      type: ActionTypes.media.uploadMediaAsset.START,
      mediaUploadReference: {
        assetArrayBuffer: mediaService?.videoPending?.assetArrayBuffer,
        type: mediaService?.videoPending?.type,
        uploadUrl: mediaService?.videoPending?.uploadUrl,
      },
    });
  };

  // const _setVideoDimensions = (file: File) => {
  //   const url = URL.createObjectURL(file);
  //   const video = document.createElement('video');
  //   video.onloadedmetadata = () => {
  //     // Revoke when you don't need the url any more to release any reference
  //     URL.revokeObjectURL(url);
  //     _handleCropComplete({
  //       croppedArea: {
  //         x: 0, y: 0, width: video.videoWidth || 100, height: video.videoHeight || 100,
  //       },
  //       croppedAreaPixels: {
  //         x: 0,
  //         y: 0,
  //         height: video.videoHeight || 1080,
  //         width: video.videoWidth || 1080,
  //       },
  //     });
  //   };
  //   video.src = url;
  //   video.load();
  // };

  const _showVideoTitle = () => (mediaService?.croppedVideo?.src || (existingVideo?.uri && !isChanging)) && (destination !== 'contentVideo' && destination !== 'originalVideo');

  const hasCroppedVideo = mediaService?.croppedVideo?.src?.length > 0;
  const videoFileUrl = hasCroppedVideo ? mediaService?.croppedVideo?.src
    : (mediaService?.originalVideo?.sources?.original?.url || existingVideo?.uri);
  const videoThumbnail = hasCroppedVideo ? mediaService?.croppedVideo?.thumbnail
    : (mediaService?.originalVideo?.sources?.thumbnail?.url || !existingVideo?.thumbnail?.uri);

  const isLoading = ((!!mediaService?.videoPending?.fetching || !!mediaService?.originalVideo?.fetching) && mediaService?.videoPending?.uploadStatus !== 'error');

  /**
   * Function to grab the file from machine and
   * convert to Data URL FOR NOW.
   */
  const _getRawFile = async (file: File) => {
    /* Sometimes a re-render will cause this to be called with an undefined file */
    if (!file) return;
    await setUploadedVideo(file);
    const reader = new FileReader();
    reader.onload = () => {
      setRawVideo(reader.result);
      if (skipCropStep) return;
      _openCropper();
    };
    try {
      await reader.readAsDataURL(file);
      if (skipCropStep) _finishCrop(file);
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.changed_video_file,
      });
    } catch (e) {
      // console.error('error with reader.readAsDataURL');
    }
  };

  // If we're skipping the cropper, watch for croppedArea to be set by the video file dimensions
  // useEffect(() => {
  //   if (skipCropStep && croppedAreaPixels?.width && uploadedVideo) {
  //     _finishCrop();
  //   }
  // }, [croppedAreaPixels, skipCropStep]);

  return (
    <>
      <StyledModal open={open} viewport={viewport}>
        {/* LOADING STATE */}
        {isLoading && (
          <ModalContent center fullHeight>
            <StyledHeadingWrapper>
              <Heading small color={Colors.GrayVeryDark}>
                Uploading Video
              </Heading>
            </StyledHeadingWrapper>
            <LoadingSpinner size="medium" />

          </ModalContent>
        )}
        {/* EDITING STATE  */}
        {!isLoading && (
          <>
            <ModalContent>
              <StyledHeadingWrapper>
                <Heading medium color={Colors.GrayVeryDark}>
                  Upload video
                </Heading>
              </StyledHeadingWrapper>
              {!shouldRenderCropper && (
                <>
                  {_renderVideoSection(videoFileUrl, videoThumbnail)}
                  <UploadButtonsSection isMobile={isMobile} viewport={viewport}>
                    {_showVideoTitle() && (
                      <Controller
                        control={control}
                        name="videoTitle"
                        defaultValue={existingVideo?.title ?? ''}
                        rules={{
                          required: true,
                        }}
                        render={({ onChange, value }) => (
                          <InputWrapper>
                            <Input
                              variant="text"
                              required
                              inputName="videoTitle"
                              label="Video Title"
                              value={value}
                              onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                                (event?.target: HTMLInputElement);
                                onChange(event);
                              }}
                            />
                          </InputWrapper>
                        )}
                      />
                    )}
                    <UploadButtonsWrapper isMobile={isMobile} viewport={viewport}>
                      {(!videoFileUrl
                      && (!existingVideo?.uri || isChanging)) && (
                        <Input
                          width={BUTTON_WIDTH}
                          variant="file"
                          inputName="photo-upload"
                          accept="video/mp4, video/webm, video/ogg"
                          onChange={(file) => _getRawFile(file)}
                          renderButtonPrimary={_renderInputFileButton}
                        >
                          Choose a video
                        </Input>
                      )}
                      {(videoFileUrl || (existingVideo?.uri && !isChanging)) && (
                        <StyledButton
                          isMobile={isMobile}
                          disabled={!isDirty && destination !== 'contentVideo' && destination !== 'originalVideo'}
                          variant="primary"
                          width={BUTTON_WIDTH}
                          onClick={handleSubmit(_onSave)}
                        >
                          Save
                        </StyledButton>
                      )}
                      <StyledButton
                        variant="primary"
                        lastChild
                        inverted
                        width={BUTTON_WIDTH}
                        onClick={_onCancel}
                      >
                        Cancel
                      </StyledButton>

                    </UploadButtonsWrapper>
                  </UploadButtonsSection>
                </>
              )}
              {shouldRenderCropper && (
                <>
                  <CropperWrapper>
                    <MediaCropper
                      variant="video"
                      zoomWithScroll={false}
                      source={rawVideo}
                      aspectRatio={1}
                      zoom={zoom}
                      objectFit="contain"
                      onCropComplete={_handleCropComplete}
                      style={{
                        containerStyle: {
                          height: '100%',
                          width: '100%',
                          // maxHeight: `calc(90vh - ${maxModalHeightOffset}px)`,
                        },
                      }}
                    />
                  </CropperWrapper>
                  <CropperControlsWrapper viewport={viewport}>
                    <Input
                      variant="slider"
                      label="Zoom"
                      inputName="photo-zoom-slider"
                      min={1}
                      max={3}
                      step={0.01}
                      value={zoom}
                      width="100%"
                      onChange={_handleOnZoomChange}
                    />
                    <CropperButtonsWrapper isMobile={isMobile} viewport={viewport}>
                      <StyledButton
                        variant="primary"
                        isMobile={isMobile}
                        width={BUTTON_WIDTH}
                        onClick={_finishCrop}
                      >
                        Crop
                      </StyledButton>
                      <StyledButton
                        variant="primary"
                        lastChild
                        inverted
                        width={BUTTON_WIDTH}
                        onClick={_closeCropper}
                      >
                        Cancel
                      </StyledButton>
                    </CropperButtonsWrapper>
                  </CropperControlsWrapper>
                </>
              )}
            </ModalContent>
          </>
        )}
        {/* ERROR STATE */}
        {!mediaService?.videoPending?.fetching && mediaService?.videoPending?.uploadStatus === 'error' && (
          <ModalContent center fullHeight>
            <Icon medium name="clear" color={Colors.SecondaryRed} />
            <SmallHeadingWrapper>
              <Heading small color={Colors.GrayVeryDark}>
                Oh no! Your video failed to upload.
              </Heading>
            </SmallHeadingWrapper>
            <Body medium color={Colors.GrayDark}>Network error occurred.</Body>
            <RetryButtonsWrapper isMobile={isMobile} viewport={viewport}>
              <StyledButton
                isMobile={isMobile}
                variant="primary"
                width={BUTTON_WIDTH}
                onClick={_retryUpload}
              >
                Retry
              </StyledButton>
              <StyledButton
                variant="primary"
                lastChild
                inverted
                width={BUTTON_WIDTH}
                onClick={_onCancel}
              >
                Cancel
              </StyledButton>

            </RetryButtonsWrapper>
          </ModalContent>
        )}
      </StyledModal>
    </>
  );
};

export default (withAppManager(UploadVideoModal): React$ComponentType<ManagedProps<Props>>);
